import { useMsal } from '@azure/msal-react';
import { useCallback } from 'react';
import { apiRequest, allowedTenantIds } from '../config/authConfig';

function getAccountTenantId(account) {
  return String(account?.tenantId || account?.idTokenClaims?.tid || '').toLowerCase();
}

function makeAuthError(message, code) {
  const error = new Error(message);
  error.status = 401;
  error.code = code;
  return error;
}

function makeForbiddenAuthError(message, code) {
  const error = new Error(message);
  error.status = 403;
  error.code = code;
  return error;
}

function isUnsupportedAccountTypeError(error) {
  const message = String(error?.message || '').toLowerCase();
  const errorCode = String(error?.errorCode || error?.code || '').toLowerCase();
  return (
    message.includes('aadsts500207') ||
    message.includes("account type can't be used for the resource") ||
    errorCode === '500207'
  );
}

function isInvalidScopeError(error) {
  const message = String(error?.message || '').toLowerCase();
  const errorCode = String(error?.errorCode || error?.code || '').toLowerCase();
  return (
    message.includes('aadsts70011') ||
    message.includes('invalid_scope') ||
    errorCode === '70011'
  );
}

const useAuthenticatedFetch = () => {
  const { instance, accounts, inProgress } = useMsal();

  const authenticatedFetch = useCallback(async (url, options = {}) => {
    //console.log('AuthenticatedFetch: Starting request for', url);
    // console.log('AuthenticatedFetch: Current state:', { 
    //   inProgress, 
    //   accountsCount: accounts?.length || 0,
    //   firstAccount: accounts?.[0]?.username || 'none'
    // });
    
    // Check if authentication is in progress or no accounts exist
    if (inProgress !== 'none') {
      console.warn('AuthenticatedFetch: Authentication in progress, current state:', inProgress);
      throw makeAuthError(`Authentication in progress (${inProgress}). Please wait and try again.`, 'auth_in_progress');
    }
    
    if (!accounts || accounts.length === 0) {
      console.error('AuthenticatedFetch: No authenticated account found');
      throw makeAuthError('You are not logged in. Please sign in to continue.', 'no_account');
    }

    const tenantId = getAccountTenantId(accounts[0]);
    if (allowedTenantIds.length > 0 && (!tenantId || !allowedTenantIds.includes(tenantId))) {
      throw makeForbiddenAuthError(
        'Your organization is not authorized to use RIIMS yet.',
        'tenant_not_allowed'
      );
    }

    try {
      // Get access token for your API
      const request = {
        ...apiRequest,
        account: accounts[0]
      };

      //console.log('AuthenticatedFetch: Attempting to acquire token silently...');
      const response = await instance.acquireTokenSilent(request);
      //console.log('AuthenticatedFetch: Token acquired successfully');
      
      // Add token to headers
      const headers = {
        'Authorization': `Bearer ${response.accessToken}`,
        ...options.headers
      };

      // Only set Content-Type to application/json if it's not FormData
      // For FormData, let the browser set the correct Content-Type with boundary
      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }

      //console.log('AuthenticatedFetch: Making authenticated request to', url);
      const fetchResponse = await fetch(url, {
        ...options,
        headers
      });
      
      //console.log('AuthenticatedFetch: Request completed with status:', fetchResponse.status);
      return fetchResponse;
      
    } catch (error) {
      console.error('AuthenticatedFetch: Error occurred:', error);

      // Preserve explicit aborts (e.g., caller timeout) as-is.
      if (error?.name === 'AbortError') {
        throw error;
      }
      
      // Check if error indicates the user needs to re-authenticate
      const errorCode = error?.errorCode || error?.code;
      const message = error?.message || '';
      const needsInteraction =
        error?.name === 'InteractionRequiredAuthError' ||
        errorCode === 'interaction_required' ||
        errorCode === 'login_required' ||
        errorCode === 'consent_required' ||
        message.includes('interaction_required') ||
        message.includes('consent_required') ||
        message.includes('login_required');

      if (needsInteraction) {
        // Consent problems and an expired session both surface as "interaction
        // required", but they need opposite handling: consent may require an
        // admin, so bouncing the user through a redirect achieves nothing and is
        // worth telling them about. An expired session is routine and should just
        // be re-acquired. Keep them distinguishable for the caller.
        const isConsentProblem =
          errorCode === 'consent_required' || message.includes('consent_required');

        if (isConsentProblem) {
          console.log('AuthenticatedFetch: consent required');
          throw makeAuthError('Additional consent is required for this application.', 'consent_required');
        }

        console.log('AuthenticatedFetch: session expired, interactive re-auth required');
        throw makeAuthError('Your session has expired. Signing you back in...', 'interaction_required');
      }

      if (isUnsupportedAccountTypeError(error)) {
        throw makeForbiddenAuthError(
          'Your signed-in account cannot request an access token for this API resource (AADSTS500207). Please verify API scope/resource configuration for this environment.',
          'unsupported_account_type'
        );
      }

      if (isInvalidScopeError(error)) {
        throw makeForbiddenAuthError(
          'The requested API scope is not valid for this tenant (AADSTS70011). This tenant is either not allowed or has not granted consent to the API permission.',
          'invalid_scope'
        );
      }
      
      // For other errors, provide more specific messaging
      if (error.message?.includes('network') || error.name === 'NetworkError') {
        throw new Error('Network error occurred. Please check your connection and try again.');
      }
      
      // Generic authentication error
      throw makeForbiddenAuthError(
        'Authentication failed while acquiring an API token. Please verify auth configuration and sign in again.',
        'auth_failed'
      );
    }
  }, [instance, accounts, inProgress]);

  return authenticatedFetch;
};

export default useAuthenticatedFetch;